import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '@stores/translationStore';
import { apiClient } from '@services/api';
import PageSpinner from '@components/PageSpinner';
import PageHeader from '@components/PageHeader';
import SectionTitle from '@components/SectionTitle';
import './ProfilePage.scss';

interface NotEnrolled {
  enrolled: false;
  /** A room the main booker manages: no guest of its own yet. */
  managed?: boolean;
  bookerName?: string;
  available: boolean;
  canEnroll: boolean;
  chainName: string;
  pointsPerNight: number;
  suggestedEmail?: string | null;
  phone?: string | null;
}

interface Enrolled {
  enrolled: true;
  chainName: string;
  pointsPerNight: number;
  profile: { firstName?: string; lastName?: string; displayName: string; phoneE164?: string; email?: string; memberSince?: string; marketingOptIn: boolean };
  points: { balance: number };
  stays: { hotelName: string; checkIn: string; checkOut: string; nights: number; status: string; role: string; reservationHolder?: string | null }[];
  history: { type: string; points: number; nights?: number; note?: string; createdAt: string }[];
  /** Rooms they booked that are now their guests' own stays. */
  bookedForOthers?: { hotelName: string; guestName: string; checkIn: string; checkOut: string; nights: number }[];
}

type ProfileResponse = NotEnrolled | Enrolled;

const fmt = (d: string, lang: string) =>
  new Date(d).toLocaleDateString(lang, { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * The guest's profile and loyalty membership. The first visit is the invitation: join (one tap, email
 * confirmed) or go back. Joining claims the guest's profile — this stay, and any earlier stay in the chain
 * with the same phone and email — with its points.
 */
export default function ProfilePage() {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const { hotelSlug } = useParams<{ hotelSlug: string }>();
  const [data, setData] = useState<ProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [marketing, setMarketing] = useState(false);
  const [joining, setJoining] = useState(false);

  const tr = (key: string, fallback: string, params?: Record<string, string | number>) => {
    const v = t(key, params);
    if (v && v !== key) return v;
    return params ? Object.entries(params).reduce((s, [k, val]) => s.split(`{{${k}}}`).join(String(val)), fallback) : fallback;
  };

  const goWelcome = () => navigate(hotelSlug ? `/${hotelSlug}/welcome` : '/welcome');

  useEffect(() => {
    const controller = new AbortController();
    window.scrollTo(0, 0);
    apiClient.get<ProfileResponse>(`/api/v1/guest/profile?lang=${language}`, { signal: controller.signal })
      .then(({ data: res }) => {
        setData(res);
        if (!res.enrolled && res.suggestedEmail) setEmail(res.suggestedEmail);
      })
      .catch(e => { if (e?.name !== 'AbortError') setError(tr('profile.loadError', 'Could not load your profile.')); })
      .finally(() => setLoading(false));
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const join = async () => {
    setJoining(true);
    setError(null);
    try {
      const { data: res } = await apiClient.post<Enrolled>(`/api/v1/guest/profile/enroll?lang=${language}`, { email, marketingOptIn: marketing });
      setData(res);
    } catch (e: unknown) {
      const message = (e as { message?: string })?.message;
      setError(message || tr('profile.joinError', 'Could not join. Please try again.'));
    } finally {
      setJoining(false);
    }
  };

  if (loading) return <PageSpinner />;

  return (
    <div className="page-content profile-page">
      <PageHeader title={tr('profile.title', 'My profile')} subtitle={data?.chainName || undefined} />

      {error && <div className="profile-error">{error}</div>}

      {data && !data.enrolled && (
        <div className="profile-join">
          <div className="profile-join__badge">★</div>
          <h2>{tr('profile.joinTitle', 'Join {{chain}} Rewards', { chain: data.chainName || '' })}</h2>
          <p>{tr('profile.joinBody', 'Earn {{points}} points for every night you stay with us, at any of our hotels. Your stays and points are kept in your profile.', { points: data.pointsPerNight })}</p>

          {data.managed ? (
            <p className="profile-join__note">
              {tr('profile.managedRoom', "This room is part of {{booker}}'s booking. To earn points for your stay, ask {{booker}} or the front desk to give this room your own phone number.", { booker: data.bookerName || '' })}
            </p>
          ) : !data.available ? (
            <p className="profile-join__note">{tr('profile.unavailable', 'Rewards are not available at this property.')}</p>
          ) : !data.canEnroll ? (
            <p className="profile-join__note">{tr('profile.verifyFirst', 'Verify your phone number on this device to join.')}</p>
          ) : (
            <>
              {data.phone && (
                <div className="profile-field">
                  <span className="profile-field__label">{tr('profile.phone', 'Phone')}</span>
                  <span dir="ltr">{data.phone}</span>
                </div>
              )}
              <label className="profile-field">
                <span className="profile-field__label">{tr('profile.email', 'Email')}</span>
                <input type="email" dir="ltr" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@example.com" />
              </label>
              <label className="profile-check">
                <input type="checkbox" checked={marketing} onChange={e => setMarketing(e.target.checked)} />
                <span>{tr('profile.marketing', 'Send me offers and news (optional)')}</span>
              </label>
              <p className="profile-join__fine">
                {tr('profile.consent', 'By joining you agree that we keep your profile, stays and points until you ask us to remove them.')}
              </p>
              <button className="profile-btn profile-btn--primary" onClick={join} disabled={joining || !email.trim()}>
                {joining ? tr('common.loading', 'Loading…') : tr('profile.join', 'Join')}
              </button>
            </>
          )}
          <button className="profile-btn profile-btn--ghost" onClick={goWelcome}>{tr('profile.notNow', 'Not now')}</button>
        </div>
      )}

      {data && data.enrolled && (
        <>
          <div className="profile-points">
            <div className="profile-points__value">{data.points.balance.toLocaleString(language)}</div>
            <div className="profile-points__label">{tr('profile.points', 'points')}</div>
            {data.profile.memberSince && (
              <div className="profile-points__since">
                {tr('profile.memberSince', 'Member since {{date}}', { date: fmt(data.profile.memberSince, language) })}
              </div>
            )}
          </div>

          <SectionTitle>{tr('profile.details', 'My details')}</SectionTitle>
          <div className="profile-card">
            <div className="profile-field"><span className="profile-field__label">{tr('profile.name', 'Name')}</span><span>{data.profile.displayName}</span></div>
            {data.profile.phoneE164 && <div className="profile-field"><span className="profile-field__label">{tr('profile.phone', 'Phone')}</span><span dir="ltr">{data.profile.phoneE164}</span></div>}
            {data.profile.email && <div className="profile-field"><span className="profile-field__label">{tr('profile.email', 'Email')}</span><span dir="ltr">{data.profile.email}</span></div>}
          </div>

          <SectionTitle>{tr('profile.stays', 'My stays')}</SectionTitle>
          <div className="profile-card">
            {data.stays.length === 0
              ? <div className="profile-empty">{tr('profile.noStays', 'No stays yet.')}</div>
              : data.stays.map((s, i) => (
                <div key={i} className="profile-row">
                  <div>
                    <div className="profile-row__title">{s.hotelName}</div>
                    <div className="profile-row__sub">{fmt(s.checkIn, language)} – {fmt(s.checkOut, language)}</div>
                    {s.role === 'split_guest' && s.reservationHolder && (
                      <div className="profile-row__sub">{tr('profile.onReservationOf', "On {{name}}'s reservation", { name: s.reservationHolder })}</div>
                    )}
                  </div>
                  <span className="profile-row__meta">{tr('profile.nights', '{{n}} nights', { n: s.nights })}</span>
                </div>
              ))}
          </div>

          {(data.bookedForOthers?.length ?? 0) > 0 && (
            <>
              <SectionTitle>{tr('profile.bookedForOthers', 'Booked by you')}</SectionTitle>
              <div className="profile-card">
                {data.bookedForOthers!.map((s, i) => (
                  <div key={i} className="profile-row">
                    <div>
                      <div className="profile-row__title">{s.guestName}</div>
                      <div className="profile-row__sub">{s.hotelName} · {fmt(s.checkIn, language)} – {fmt(s.checkOut, language)}</div>
                    </div>
                    <span className="profile-row__meta">{tr('profile.nights', '{{n}} nights', { n: s.nights })}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          <SectionTitle>{tr('profile.history', 'Points history')}</SectionTitle>
          <div className="profile-card">
            {data.history.length === 0
              ? <div className="profile-empty">{tr('profile.noPoints', 'Your points appear here after each stay.')}</div>
              : data.history.map((h, i) => (
                <div key={i} className="profile-row">
                  <div>
                    <div className="profile-row__title">
                      {h.type === 'Earn' ? tr('profile.earned', 'Stay') : h.type === 'Adjust' ? tr('profile.adjusted', 'Adjustment') : h.type}
                    </div>
                    <div className="profile-row__sub">{fmt(h.createdAt, language)}{h.note ? ` · ${h.note}` : ''}</div>
                  </div>
                  <span className={`profile-row__points ${h.points < 0 ? 'is-negative' : ''}`}>
                    {h.points > 0 ? '+' : ''}{h.points.toLocaleString(language)}
                  </span>
                </div>
              ))}
          </div>
        </>
      )}
    </div>
  );
}
